import { Controller } from "@hotwired/stimulus"
import { OBJECT_TYPES, CATEGORIES, typesByCategory } from "../editor/object_types"
import { editorStore } from "../editor/store"

// Renders the object library and wires TWO ways to add an object:
//   • HTML5 drag-n-drop (works on mouse and most styluses; owner of the
//     drop side is editor_controller.js)
//   • Click-to-arm + tap-on-stage — needed for touch devices where drag
//     is unreliable. Click sets editorStore.pendingAddType; the stage
//     click handler in editor_controller consumes it.
//
// DOM is built via createElement/textContent (not innerHTML) so future i18n
// or user-defined type names can't introduce XSS.
export default class extends Controller {
  connect() {
    this.#render()
    this._onPending = () => this.#syncPending()
    editorStore.addEventListener("pending-add:change", this._onPending)
    this.#syncPending()
  }

  disconnect() {
    if (this._onPending) editorStore.removeEventListener("pending-add:change", this._onPending)
  }

  #render() {
    this.element.replaceChildren()
    const grouped = typesByCategory()
    for (const [key, label] of Object.entries(CATEGORIES)) {
      const items = grouped[key] || []
      if (items.length === 0) continue
      const section = document.createElement("section")
      section.className = "mb-3"
      const title = document.createElement("h3")
      title.className = "px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-neutral-600 font-semibold"
      title.textContent = label
      const list = document.createElement("ul")
      for (const t of items) list.appendChild(this.#item(t))
      section.append(title, list)
      this.element.appendChild(section)
    }
  }

  #item(t) {
    const li = document.createElement("li")
    li.draggable = true
    li.dataset.typeId = t.id
    // py-2 → ~44pt row on mobile for HIG compliance; still comfortable on desktop.
    li.className = "flex items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-100 cursor-pointer select-none min-h-[44px]"
    li.addEventListener("dragstart", (e) => this.#onDragStart(e))
    li.addEventListener("click", () => this.#onClick(t.id))

    const swatch = document.createElement("span")
    swatch.className = "inline-block w-4 h-4 rounded-sm border shrink-0"
    swatch.style.background = t.fillColor
    swatch.style.borderColor = t.strokeColor

    const name = document.createElement("span")
    name.className = "flex-1 text-neutral-800"
    name.textContent = t.name

    const size = document.createElement("span")
    size.className = "text-xs text-neutral-500 shrink-0"
    size.textContent = `${t.defaultSizeM.width}×${t.defaultSizeM.height} м`

    li.append(swatch, name, size)
    return li
  }

  #onDragStart(event) {
    const typeId = event.currentTarget.dataset.typeId
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("application/x-plot-object-type", typeId)
  }

  #onClick(typeId) {
    // Toggle: clicking the already-armed type disarms it. Otherwise, arm.
    if (editorStore.pendingAddType === typeId) {
      editorStore.setPendingAddType(null)
    } else {
      editorStore.setPendingAddType(typeId)
      // Ask the bottom sheet to collapse so the canvas is visible for the next tap.
      this.dispatch("pending-armed", { detail: { typeId }, prefix: "library", bubbles: true })
    }
  }

  #syncPending() {
    const pending = editorStore.pendingAddType
    for (const li of this.element.querySelectorAll("li[data-type-id]")) {
      li.dataset.pending = li.dataset.typeId === pending ? "1" : "0"
    }
  }
}
